import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { SpinnerGap } from "@phosphor-icons/react";
import { ADMIN_EMAIL } from "./shared";
import { AccountInfoCard } from "@/components/workspace/AccountInfoCard";
import { BusinessListCard } from "@/components/workspace/BusinessListCard";
import { TeamMembersCard } from "@/components/workspace/TeamMembersCard";
import { DangerZoneSection } from "@/components/workspace/DangerZoneSection";

export function WorkspaceTab() {
  const { api, user, logout, activeBusiness, businesses, loadBusinesses } = useAuth();
  const isAdmin = user?.email === ADMIN_EMAIL;

  const [workspace, setWorkspace] = useState(null);
  const [loadingWorkspace, setLoadingWorkspace] = useState(true);
  const [workspaceName, setWorkspaceName] = useState("");
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [newBizName, setNewBizName] = useState("");
  const [newBizIndustry, setNewBizIndustry] = useState("");
  const [creatingBiz, setCreatingBiz] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const loadWorkspace = useCallback(async () => {
    try {
      setLoadingWorkspace(true);
      const resp = await api.get("/account");
      setWorkspace(resp.data);
      setWorkspaceName(resp.data.name || "");
    } catch { /* silent */ }
    finally { setLoadingWorkspace(false); }
  }, [api]);

  const loadDeletionStatus = useCallback(async () => {
    try {
      const resp = await api.get("/account/deletion-status");
      setDeletionStatus(resp.data?.pending ? resp.data : null);
    } catch { /* silent */ }
  }, [api]);

  useEffect(() => { loadWorkspace(); loadDeletionStatus(); }, [loadWorkspace, loadDeletionStatus]);

  const saveWorkspaceName = async () => {
    if (!workspaceName.trim()) return;
    setSavingWorkspace(true);
    try {
      await api.patch("/account", { name: workspaceName.trim() });
      toast.success("Workspace name updated");
      loadWorkspace();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    finally { setSavingWorkspace(false); }
  };

  const inviteMember = async (payload) => {
    try {
      await api.post("/account/invite", payload);
      toast.success(`Invited ${payload.email}`);
      loadWorkspace();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to invite"); }
  };

  const removeMember = async (membershipId) => {
    try {
      await api.delete(`/account/members/${membershipId}`);
      toast.success("Member removed");
      loadWorkspace();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove"); }
  };

  const changeRole = async (membershipId, newRole) => {
    try {
      await api.patch(`/account/members/${membershipId}/role?role=${newRole}`);
      toast.success("Role updated");
      loadWorkspace();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update role"); }
  };

  const createBusiness = async () => {
    if (!newBizName.trim()) return;
    setCreatingBiz(true);
    try {
      await api.post("/businesses", { name: newBizName.trim(), industry: newBizIndustry.trim() });
      toast.success(`Created business "${newBizName.trim()}"`);
      setNewBizName("");
      setNewBizIndustry("");
      loadBusinesses();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create business"); }
    finally { setCreatingBiz(false); }
  };

  const deleteAccount = async () => {
    if (!confirmEmail.trim()) return;
    setDeletingAccount(true);
    try {
      const resp = await api.post("/account/delete", { confirm_email: confirmEmail.trim() });
      toast.success("Account deletion scheduled. You have 72 hours to cancel.");
      setDeleteConfirm(false);
      setConfirmEmail("");
      setDeletionStatus({ pending: true, delete_at: resp.data.delete_at });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule deletion");
    }
    setDeletingAccount(false);
  };

  const cancelDeletion = async () => {
    try {
      await api.post("/account/cancel-deletion");
      toast.success("Account deletion canceled. You're safe.");
      setDeletionStatus(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel deletion");
    }
  };

  const handleExportData = async () => {
    try {
      const resp = await api.get("/account/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `quibn-data-export-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Data export downloaded");
    } catch { toast.error("Failed to export data"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="workspace-tab">
      {loadingWorkspace ? (
        <div className="flex justify-center py-12"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>
      ) : workspace ? (
        <>
          <AccountInfoCard
            workspace={workspace}
            workspaceName={workspaceName}
            onWorkspaceNameChange={setWorkspaceName}
            onSave={saveWorkspaceName}
            saving={savingWorkspace}
            isAdmin={isAdmin}
          />

          <BusinessListCard
            businesses={businesses}
            activeBusiness={activeBusiness}
            workspace={workspace}
            newBizName={newBizName}
            onNewBizNameChange={setNewBizName}
            newBizIndustry={newBizIndustry}
            onNewBizIndustryChange={setNewBizIndustry}
            onCreateBusiness={createBusiness}
            creatingBiz={creatingBiz}
          />

          <TeamMembersCard
            workspace={workspace}
            onInviteMember={inviteMember}
            onRemoveMember={removeMember}
            onChangeRole={changeRole}
          />

          <DangerZoneSection
            user={user}
            onLogout={logout}
            deletionStatus={deletionStatus}
            onCancelDeletion={cancelDeletion}
            onOpenDeleteDialog={() => setDeleteConfirm(true)}
            deleteConfirm={deleteConfirm}
            onDeleteConfirmChange={setDeleteConfirm}
            confirmEmail={confirmEmail}
            onConfirmEmailChange={setConfirmEmail}
            onDeleteAccount={deleteAccount}
            deletingAccount={deletingAccount}
            onExportData={handleExportData}
          />
        </>
      ) : (
        <div className="text-center py-12 text-[var(--text-muted)]">Failed to load workspace.</div>
      )}
    </motion.div>
  );
}
